/**
 * Convert minutes from midnight to hour and minute
 * @param minutes - Minutes from midnight (0-1439)
 * @returns Object with hour and minute
 */
export const minutesToTime = (minutes: number): { hour: number; minute: number } => {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return { hour, minute };
};

/**
 * Convert hour and minute to minutes from midnight
 * @param hour - Hour (0-23)
 * @param minute - Minute (0-59)
 * @returns Minutes from midnight
 */
export const timeToMinutes = (hour: number, minute: number): number => {
  return hour * 60 + minute;
};

/**
 * Convert minutes from midnight to time string (HH:MM:SS)
 * @param minutes - Minutes from midnight
 * @returns Time string in HH:MM:SS format
 */
export const minutesToTimeString = (minutes: number): string => {
  const { hour, minute } = minutesToTime(minutes);
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
};

/**
 * Convert minutes from midnight to time object for API
 * @param minutes - Minutes from midnight
 * @returns TimeObject with hour, minute, second
 */
export const minutesToTimeObject = (
  minutes: number
): { hour: number; minute: number; second: number } => {
  const { hour, minute } = minutesToTime(minutes);
  return { hour, minute, second: 0 };
};

/**
 * Convert minutes from midnight to display time string (e.g., "9:00 AM")
 * @param minutes - Minutes from midnight
 * @returns Formatted time string for display
 */
export const minutesToDisplayTime = (minutes: number): string => {
  const { hour, minute } = minutesToTime(minutes);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const displayMinute = minute.toString().padStart(2, '0');
  return `${displayHour}:${displayMinute} ${period}`;
};

/**
 * Validate if a time range has a minimum window
 * @param startTime - Start time in minutes from midnight
 * @param endTime - End time in minutes from midnight
 * @param minimumWindow - Minimum window in minutes
 * @returns Whether the range meets the minimum window requirement
 */
export const isValidTimeRange = (
  startTime: number,
  endTime: number,
  minimumWindow: number = 15
): boolean => {
  return endTime - startTime >= minimumWindow;
};
